/**
 * Policies v3 — every external-auditor and audit-report route, as one plain typed function (no hooks).
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform; an organisation invites an
 * outside auditor to read the proof that its own staff acknowledged its policies, and builds the audit report that
 * proof is handed over in. Contract: CONTRACT.md P09 (both route tables) and lib/policies/types.ts (shapes).
 *
 * COMPONENTS NEVER CALL THIS FILE. The hooks beside it own the query keys, the normalising and the invalidation:
 * audit-hooks.ts (the auditor's own door) and audit-admin-hooks.ts (the admin's Auditor access section and the
 * internal report builder).
 *
 * Two realms, two transports:
 *  - auditor (/v1/public/policy-audit/*): `auditorFetch`, the `X-Policy-Audit-Token` header, nothing of the admin's.
 *    Tenancy comes from the grant row on the server; no route here names a workspace.
 *  - admin   (/v1/policies/auditors, /v1/policies/audit-reports): `fetchApi` (the admin session + x-workspace-id).
 *    Files go through `adminAuditBlob`.
 *
 * A report's file is addressed by its KIND (POLICY_AUDIT_REPORT_FILE_KINDS, with the spaces: "Audit Report"), encoded.
 */
import { buildQueryString, fetchApi } from '@/lib/api/client'
import { auditorFetch, auditorFetchBlob, adminAuditBlob, type AuditFileBlob } from './audit-fetch'
import type {
  CreateAuditorReportBody,
  CreatePolicyAuditReportBody,
  InvitePolicyAuditorBody,
  PolicyAsOf,
  PolicyAsOfQuery,
  PolicyAuditorCodeBody,
  PolicyAuditorCodeSent,
  PolicyAuditorMe,
  PolicyAuditorOverview,
  PolicyAuditorPolicy,
  PolicyAuditorPolicyRow,
  PolicyAuditorRow,
  PolicyAuditorSession,
  PolicyAuditorVerifyBody,
  PolicyAuditReportFileKind,
  PolicyAuditReportRow,
  PolicyEvidenceRecord,
  PolicyPeoplePage,
  PolicyPeopleQuery,
  UpdatePolicyAuditorBody,
} from './types'

const enc = encodeURIComponent
const AUDITOR = '/public/policy-audit'
const ADMIN_AUDITORS = '/policies/auditors'
const ADMIN_REPORTS = '/policies/audit-reports'

/** Query string without the leading '?' (the key part of a query key). */
export function auditQs(params: object | undefined): string {
  return buildQueryString(params as Record<string, unknown> | undefined).replace(/^\?/, '')
}

const withQs = (path: string, qs: string) => (qs ? `${path}?${qs}` : path)

/** A method and a JSON body: fits both `fetchApi` (RequestInit) and `auditorFetch` (no HeadersInit, no credentials). */
const json = (method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): { method: string; body?: string } => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

export const auditPaths = {
  codeSend: `${AUDITOR}/sign-in/code`,
  verify: `${AUDITOR}/sign-in/verify`,
  signOut: `${AUDITOR}/sign-out`,
  me: `${AUDITOR}/me`,
  overview: `${AUDITOR}/overview`,
  policies: `${AUDITOR}/policies`,
  policy: (policyId: string) => `${AUDITOR}/policies/${enc(policyId)}`,
  versionFile: (policyId: string, versionId: string) => `${AUDITOR}/policies/${enc(policyId)}/versions/${enc(versionId)}/file`,
  people: (policyId: string) => `${AUDITOR}/policies/${enc(policyId)}/people`,
  person: (policyId: string, attestationId: string) => `${AUDITOR}/policies/${enc(policyId)}/people/${enc(attestationId)}`,
  asOf: (policyId: string) => `${AUDITOR}/policies/${enc(policyId)}/as-of`,
  receiptPdf: (acknowledgmentId: string) => `${AUDITOR}/receipts/${enc(acknowledgmentId)}/pdf`,
  reports: `${AUDITOR}/reports`,
  report: (reportId: string) => `${AUDITOR}/reports/${enc(reportId)}`,
  reportFile: (reportId: string, kind: PolicyAuditReportFileKind) => `${AUDITOR}/reports/${enc(reportId)}/files/${enc(kind)}`,
  adminAuditors: ADMIN_AUDITORS,
  adminAuditor: (grantId: string) => `${ADMIN_AUDITORS}/${enc(grantId)}`,
  adminReports: ADMIN_REPORTS,
  adminReport: (reportId: string) => `${ADMIN_REPORTS}/${enc(reportId)}`,
  adminReportFile: (reportId: string, kind: PolicyAuditReportFileKind) => `${ADMIN_REPORTS}/${enc(reportId)}/files/${enc(kind)}`,
} as const

// ═════════════════════════════════════════════════════════════════════════════
// The auditor's own door — /v1/public/policy-audit
// ═════════════════════════════════════════════════════════════════════════════

export const auditorApi = {
  /** Sign-in step 1: ask for a one-time code. The same answer whether or not the address is the grant's. */
  sendCode: (body: PolicyAuditorCodeBody) => auditorFetch<PolicyAuditorCodeSent>(auditPaths.codeSend, { ...json('POST', body), auth: false }),
  /** Sign-in step 2: the code earns the session token. */
  verify: (body: PolicyAuditorVerifyBody) => auditorFetch<PolicyAuditorSession>(auditPaths.verify, { ...json('POST', body), auth: false }),
  signOut: () => auditorFetch<void>(auditPaths.signOut, json('POST', {})),
  me: () => auditorFetch<PolicyAuditorMe>(auditPaths.me),
  overview: () => auditorFetch<PolicyAuditorOverview>(auditPaths.overview),
  policies: () => auditorFetch<PolicyAuditorPolicyRow[]>(auditPaths.policies),
  policy: (policyId: string) => auditorFetch<PolicyAuditorPolicy>(auditPaths.policy(policyId)),
  people: (policyId: string, query: PolicyPeopleQuery) => auditorFetch<PolicyPeoplePage>(withQs(auditPaths.people(policyId), auditQs(query))),
  person: (policyId: string, attestationId: string) => auditorFetch<PolicyEvidenceRecord>(auditPaths.person(policyId, attestationId)),
  asOf: (policyId: string, query: PolicyAsOfQuery) => auditorFetch<PolicyAsOf>(withQs(auditPaths.asOf(policyId), auditQs(query))),
  reports: () => auditorFetch<PolicyAuditReportRow[]>(auditPaths.reports),
  report: (reportId: string) => auditorFetch<PolicyAuditReportRow>(auditPaths.report(reportId)),
  createReport: (body: CreateAuditorReportBody) => auditorFetch<PolicyAuditReportRow>(auditPaths.reports, json('POST', body)),
}

/** A policy version's PDF, as bytes (the viewer draws it) — read by the auditor, logged by the API before it is served. */
export function fetchAuditorVersionFile(policyId: string, versionId: string): Promise<AuditFileBlob> {
  return auditorFetchBlob(auditPaths.versionFile(policyId, versionId), 'policy.pdf')
}

/** The receipt PDF of one acknowledgment (the id is the evidence record's `acknowledgment.evidenceId`). */
export function fetchAuditorReceiptPdf(acknowledgmentId: string): Promise<AuditFileBlob> {
  return auditorFetchBlob(auditPaths.receiptPdf(acknowledgmentId), 'policy-receipt.pdf')
}

/** One file of a built report (`Zip` = everything in one archive). */
export function fetchAuditorReportFile(reportId: string, kind: PolicyAuditReportFileKind): Promise<AuditFileBlob> {
  return auditorFetchBlob(auditPaths.reportFile(reportId, kind), kind === 'Zip' ? 'Audit report.zip' : 'audit-report')
}

// ═════════════════════════════════════════════════════════════════════════════
// The admin's side — Settings -> Auditor access, and Policies -> Reports
// ═════════════════════════════════════════════════════════════════════════════

export const auditAdminApi = {
  auditors: () => fetchApi<PolicyAuditorRow[]>(auditPaths.adminAuditors),
  invite: (body: InvitePolicyAuditorBody) => fetchApi<PolicyAuditorRow>(auditPaths.adminAuditors, json('POST', body)),
  updateAuditor: (grantId: string, body: UpdatePolicyAuditorBody) => fetchApi<PolicyAuditorRow>(auditPaths.adminAuditor(grantId), json('PATCH', body)),
  revoke: (grantId: string) => fetchApi<PolicyAuditorRow>(`${auditPaths.adminAuditor(grantId)}/revoke`, json('POST', {})),
  resend: (grantId: string) => fetchApi<PolicyAuditorRow>(`${auditPaths.adminAuditor(grantId)}/resend`, json('POST', {})),
  reports: () => fetchApi<PolicyAuditReportRow[]>(auditPaths.adminReports),
  report: (reportId: string) => fetchApi<PolicyAuditReportRow>(auditPaths.adminReport(reportId)),
  createReport: (body: CreatePolicyAuditReportBody) => fetchApi<PolicyAuditReportRow>(auditPaths.adminReports, json('POST', body)),
  deleteReport: (reportId: string) => fetchApi<void>(auditPaths.adminReport(reportId), json('DELETE')),
}

/** One file of a report the organisation built (admin session + workspace header). */
export function fetchAdminReportFile(reportId: string, kind: PolicyAuditReportFileKind): Promise<AuditFileBlob> {
  return adminAuditBlob(auditPaths.adminReportFile(reportId, kind), kind === 'Zip' ? 'Audit report.zip' : 'audit-report')
}
